import { normalizeNextActionPayload } from '../types/nextAction';
import {
    normalizeExecutionId,
    normalizeRequestId,
    normalizeSubjectId
} from '../types/identifiers';
import { normalizeAnalysisPayload } from './analysisPayloadMapper';

const toTrimmedString = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
};

const toBoolean = (value) => value === true;

export function normalizePendingAiExecution(input = null) {
    if (!input || typeof input !== 'object') {
        return null;
    }

    const jobId = toTrimmedString(input.jobId || input.job_id || input.id);
    const requestId = normalizeRequestId(input.requestId || input.request_id);
    const retryRequestId = normalizeRequestId(input.retryRequestId || input.retry_request_id);
    const executionId = normalizeExecutionId(input.executionId || input.execution_id);
    const subjectId = normalizeSubjectId(input.subjectId || input.subject_id);
    const status = toTrimmedString(input.status);
    const aiDispatchState = toTrimmedString(input.aiDispatchState || input.ai_dispatch_state);
    const operationType = toTrimmedString(input.operationType || input.operation_type);
    const aiEffectiveSourceKind = toTrimmedString(
        input.aiEffectiveSourceKind || input.ai_effective_source_kind
    );
    const aiEffectiveSourceHashId = toTrimmedString(
        input.aiEffectiveSourceHashId || input.ai_effective_source_hash_id
    );
    const targetGeneration = input.targetGeneration ?? input.target_generation ?? null;
    const nextAction = normalizeNextActionPayload(input.nextAction || input.next_action, {
        jobId,
        subjectId,
        executionId,
        retryRequestId
    });
    const retryWithSameRequestId = toBoolean(input.retryWithSameRequestId) || toBoolean(input.retry_with_same_request_id);
    const failureCode = toTrimmedString(input.failureCode || input.failure_code);
    const recoveryMode = toTrimmedString(input.recoveryMode || input.recovery_mode);
    const outputLanguage = toTrimmedString(input.outputLanguage || input.output_language || input.meta?.output_language || input.meta?.outputLanguage);

    return {
        ...input,
        id: toTrimmedString(input.id) || jobId,
        jobId,
        job_id: jobId,
        requestId,
        request_id: requestId,
        retryRequestId,
        retry_request_id: retryRequestId,
        executionId,
        execution_id: executionId,
        subjectId,
        subject_id: subjectId,
        status,
        aiDispatchState,
        ai_dispatch_state: aiDispatchState,
        operationType,
        operation_type: operationType,
        targetGeneration,
        target_generation: targetGeneration,
        requestable: toBoolean(input.requestable),
        retryable: toBoolean(input.retryable),
        retryWithSameRequestId,
        retry_with_same_request_id: retryWithSameRequestId,
        failureCode,
        failure_code: failureCode,
        recoveryMode,
        recovery_mode: recoveryMode,
        aiEffectiveSourceKind,
        ai_effective_source_kind: aiEffectiveSourceKind,
        aiEffectiveSourceHashId,
        ai_effective_source_hash_id: aiEffectiveSourceHashId,
        outputLanguage,
        output_language: outputLanguage,
        nextAction,
        next_action: nextAction,
        meta: input.meta || null
    };
}

export function normalizeCurrentVisibleResult(input = null) {
    if (!input || typeof input !== 'object') {
        return null;
    }

    const resultId = toTrimmedString(input.resultId || input.result_id || input.id);
    const status = toTrimmedString(input.status);
    const analysisMarkdown = input.analysisMarkdown ?? input.analysis_markdown ?? null;
    const analysisPayload = normalizeAnalysisPayload(input.analysisPayload || input.analysis_payload);
    const generation = input.generation ?? input.result_generation ?? null;
    const accessMode = toTrimmedString(input.accessMode || input.access_mode);
    const grantedAt = toTrimmedString(input.grantedAt || input.granted_at);
    const supersedesResultId = toTrimmedString(input.supersedesResultId || input.supersedes_result_id);
    const upgradeGeneration = input.upgradeGeneration ?? input.upgrade_generation ?? null;
    const aiEffectiveSourceKind = toTrimmedString(
        input.aiEffectiveSourceKind || input.ai_effective_source_kind
    );
    const aiEffectiveSourceHashId = toTrimmedString(
        input.aiEffectiveSourceHashId || input.ai_effective_source_hash_id
    );
    const outputLanguage = toTrimmedString(input.outputLanguage || input.output_language);

    return {
        ...input,
        id: toTrimmedString(input.id) || resultId,
        resultId,
        result_id: resultId,
        status,
        generation,
        result_generation: generation,
        analysisMarkdown,
        analysis_markdown: analysisMarkdown,
        analysisPayload,
        analysis_payload: analysisPayload,
        accessMode,
        access_mode: accessMode,
        grantedAt,
        granted_at: grantedAt,
        supersedesResultId,
        supersedes_result_id: supersedesResultId,
        upgradeGeneration,
        upgrade_generation: upgradeGeneration,
        aiEffectiveSourceKind,
        ai_effective_source_kind: aiEffectiveSourceKind,
        aiEffectiveSourceHashId,
        ai_effective_source_hash_id: aiEffectiveSourceHashId,
        outputLanguage,
        output_language: outputLanguage
    };
}

export function normalizeUpgradeOffer(offer = null) {
    if (!offer || typeof offer !== 'object') {
        return null;
    }

    const upgradeClass = toTrimmedString(offer.upgrade_class || offer.upgradeClass);
    const runCode = toTrimmedString(offer.run_code || offer.runCode);
    const targetGeneration = offer.target_generation ?? offer.targetGeneration ?? null;
    const targetResultId = toTrimmedString(offer.target_result_id || offer.targetResultId);
    const currentUnlockedGeneration = offer.current_unlocked_generation ?? offer.currentUnlockedGeneration ?? null;
    const targetResultExists = toBoolean(offer.target_result_exists) || toBoolean(offer.targetResultExists);
    const displayPrice = offer.displayPrice ?? offer.display_price ?? null;

    return {
        ...offer,
        upgradeClass,
        upgrade_class: upgradeClass,
        runCode,
        run_code: runCode,
        targetGeneration,
        target_generation: targetGeneration,
        targetResultExists,
        target_result_exists: targetResultExists,
        targetResultId,
        target_result_id: targetResultId,
        currentUnlockedGeneration,
        current_unlocked_generation: currentUnlockedGeneration,
        displayPrice,
        display_price: displayPrice
    };
}

export function normalizeResultUpdateNotice(input = null) {
    if (!input || typeof input !== 'object') {
        return null;
    }

    const updateKind = toTrimmedString(input.updateKind || input.update_kind);
    const effectiveAt = toTrimmedString(input.effectiveAt || input.effective_at);
    const previousResultId = toTrimmedString(input.previousResultId || input.previous_result_id);
    const currentResultId = toTrimmedString(input.currentResultId || input.current_result_id);

    return {
        ...input,
        updateKind,
        update_kind: updateKind,
        effectiveAt,
        effective_at: effectiveAt,
        previousResultId,
        previous_result_id: previousResultId,
        currentResultId,
        current_result_id: currentResultId
    };
}

export function normalizeSubjectResponse(response = {}) {
    const raw = response?.raw && typeof response.raw === 'object'
        ? response.raw
        : response;
    const capabilities = Array.isArray(response?.capabilities)
        ? response.capabilities
        : (Array.isArray(raw?.capabilities) ? raw.capabilities : []);
    const unlockableCapabilities = Array.isArray(response?.unlockableCapabilities)
        ? response.unlockableCapabilities
        : (Array.isArray(response?.unlockable_capabilities)
            ? response.unlockable_capabilities
            : (Array.isArray(raw?.unlockable_capabilities) ? raw.unlockable_capabilities : []));
    const unavailableCapabilities = Array.isArray(response?.unavailableCapabilities)
        ? response.unavailableCapabilities
        : (Array.isArray(response?.unavailable_capabilities)
            ? response.unavailable_capabilities
            : (Array.isArray(raw?.unavailable_capabilities) ? raw.unavailable_capabilities : []));
    const inactiveGrantedCapabilities = Array.isArray(response?.inactiveGrantedCapabilities)
        ? response.inactiveGrantedCapabilities
        : (Array.isArray(response?.inactive_granted_capabilities)
            ? response.inactive_granted_capabilities
            : (Array.isArray(raw?.inactiveGrantedCapabilities)
                ? raw.inactiveGrantedCapabilities
                : (Array.isArray(raw?.inactive_granted_capabilities) ? raw.inactive_granted_capabilities : [])));

    return {
        subjectId: normalizeSubjectId(response?.subjectId || response?.subject_id || raw?.subject_id || raw?.id),
        subjectContext: response?.subjectContext || raw?.subject_context || null,
        viewerState: response?.viewerState || raw?.viewer_state || null,
        capabilities,
        unlockableCapabilities,
        unavailableCapabilities,
        inactiveGrantedCapabilities,
        aiState: response?.aiState || raw?.ai_state || null,
        currentVisibleResult: normalizeCurrentVisibleResult(
            response?.currentVisibleResult || raw?.current_visible_result || null
        ),
        pendingAiExecution: normalizePendingAiExecution(
            response?.pendingAiExecution || raw?.pending_ai_execution || null
        ),
        latestOverallResult: normalizeCurrentVisibleResult(
            response?.latestOverallResult || raw?.latest_overall_result || null
        ),
        upgradeOffer: normalizeUpgradeOffer(response?.upgradeOffer || raw?.upgrade_offer || null),
        resultUpdateNotice: normalizeResultUpdateNotice(
            response?.resultUpdateNotice || raw?.result_update_notice || null
        ),
        entitlementContext: response?.entitlementContext || raw?.entitlement_context || null,
        upsellHints: response?.upsellHints || raw?.upsell_hints || null,
        raw
    };
}

export default {
    normalizeSubjectResponse,
    normalizePendingAiExecution,
    normalizeCurrentVisibleResult,
    normalizeUpgradeOffer,
    normalizeResultUpdateNotice
};
